/**
 * The account page's "My reviews" (#118): for each world, the reader's own
 * live reviews, newest first, each linking to the review on its book page.
 *
 * Why it exists: a reader's review is shown only on its book page, and until
 * now the way back to that page was the shelf. #107 (publishing a note as a
 * review) deletes a shelf entry that held only a note, so this list is what
 * keeps a review findable. Removed reviews are not listed; the route excludes
 * them and sends no review text at all. Nothing here is visible to anyone but
 * the reader.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import type { OwnReviewsResponse } from '../../shared/reviews.ts'
import { worldPath } from '../../shared/routes.ts'
import { useI18n } from '../i18n/index.tsx'
import { mainTitle, useWorldCatalogue, useWorlds, workBySlug, worldName } from '../lib/catalogue.ts'
import { fetchOwnReviews } from '../lib/shelf.tsx'

export function AccountReviews() {
  const { t } = useI18n()
  const worlds = useWorlds()
  if (worlds.status !== 'ready') return null
  return (
    <section aria-labelledby="account-reviews-heading">
      <h2 id="account-reviews-heading">{t('account.reviews.title')}</h2>
      {worlds.worlds.map((world) => (
        <WorldReviews key={world.slug} slug={world.slug} />
      ))}
    </section>
  )
}

function WorldReviews({ slug }: { slug: string }) {
  const { t, locale } = useI18n()
  const state = useWorldCatalogue(slug)
  const [own, setOwn] = useState<OwnReviewsResponse | null>(null)

  useEffect(() => {
    let live = true
    void fetchOwnReviews(slug).then((data) => {
      if (live) setOwn(data)
    })
    return () => {
      live = false
    }
  }, [slug])

  if (state.status !== 'loaded' || own === null) return null
  const catalogue = state
  const date = (iso: string) =>
    new Intl.DateTimeFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Sofia' }).format(new Date(iso))

  return (
    <div className="account-world">
      <h3>{worldName(catalogue.universe, locale)}</h3>
      {own.reviews.length === 0 ? (
        <p>{t('account.reviews.empty')}</p>
      ) : (
        <ul className="account-reviews">
          {own.reviews.map((review) => {
            const work = workBySlug(catalogue, review.work)
            return (
              <li key={review.id}>
                <Link to={`${worldPath(slug, 'books', review.work)}#review-${String(review.id)}`}>
                  {work === undefined ? review.work : `„${mainTitle(work, locale)}“`}
                </Link>{' '}
                <span className="account-reviews__date">· {date(review.created_at)}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
